export const quickStreamRouteTemplate = `/**
 * Vibeship DevTools - Stream API Route
 * Endpoint: /api/vibeship/stream or /api/tasks/stream
 */

import { NextRequest } from 'next/server';
import { watch } from 'fs/promises';
import { join } from 'path';
import { readFileSync, existsSync, mkdirSync } from 'fs';

export const runtime = 'nodejs';
export const dynamic = 'force-dynamic';

// Store active connections
interface Client {
  controller: ReadableStreamDefaultController;
  settings: any;
}

const clients = new Map<string, Client>();

// Task scanning functions (duplicated to avoid import issues)
interface Task {
  id: string;
  type: 'checkbox' | 'todo' | 'fixme' | 'hack' | 'note' | 'bug' | 'warning';
  text: string;
  title: string;
  file: string;
  line: number;
  priority: 'low' | 'medium' | 'high';
  completed?: boolean;
  metadata?: Record<string, unknown>;
}

interface PhaseGroup {
  name: string;
  milestone: string;
  description: string;
  fileGroups: Array<{
    path: string;
    phase?: string;
    milestone?: string;
    tasks: Task[];
    progress: number;
  }>;
  totalTasks: number;
  completedTasks: number;
}

async function getProjectTasks(settings: any) {
  const tasks = await scanForTasks(settings);
  return transformToPhaseGroups(tasks, settings.scanDirectory);
}

async function scanForTasks(settings: any): Promise<Task[]> {
  const { readFileSync, existsSync } = await import('fs');
  const { join, relative } = await import('path');
  const { glob } = await import('glob');
  
  const tasks: Task[] = [];
  const cwd = process.cwd();
  const scanPath = join(cwd, settings.scanDirectory);
  
  if (!existsSync(scanPath)) {
    console.log(\`Directory \${scanPath} does not exist, returning empty tasks\`);
    return tasks;
  }
  
  try {
    const patterns = settings.filePatterns.split(',').map((p: string) => p.trim());
    
    for (const pattern of patterns) {
      const files = glob.sync(pattern, {
        cwd: scanPath,
        ignore: settings.excludePatterns.split(',').map((p: string) => p.trim()),
        absolute: false,
        nodir: true
      });
      
      for (const file of files) {
        const filePath = join(scanPath, file);
        const content = readFileSync(filePath, 'utf8');
        const relativePath = relative(cwd, filePath);
        
        if (file.endsWith('.md') || file.endsWith('.mdx')) {
          extractMarkdownTasks(content, relativePath, tasks);
        }
      }
    }
  } catch (error) {
    console.error(\`Error scanning \${scanPath}:\`, error);
  }
  
  return tasks;
}

function extractMarkdownTasks(content: string, filePath: string, tasks: Task[]): void {
  const checkboxPattern = /^\\s*[-*+]\\s*\\[([ xX])\\]\\s+(.+)$/gm;
  let taskIndex = 0;
  
  const checkboxMatches = [...content.matchAll(checkboxPattern)];
  
  for (const match of checkboxMatches) {
    const lineNumber = content.substring(0, match.index).split('\\n').length;
    const isCompleted = match[1].toLowerCase() === 'x';
    const text = match[2].trim();
    
    tasks.push({
      id: \`\${filePath}-checkbox-\${taskIndex++}\`,
      type: 'checkbox',
      text: text,
      title: text,
      file: filePath,
      line: lineNumber,
      completed: isCompleted,
      priority: 'medium',
    });
  }
}

function transformToPhaseGroups(tasks: Task[], scanDirectory: string): PhaseGroup[] {
  const { relative } = require('path');
  
  // Group tasks by directory structure
  const phaseMap = new Map<string, Map<string, Task[]>>();
  
  tasks.forEach((task) => {
    const relativePath = relative(process.cwd(), task.file);
    const pathParts = relativePath.split('/');
    
    // Determine phase name from directory structure
    let phaseName = 'General';
    
    if (pathParts.length > 1) {
      // Skip the scan directory part if present
      const startIdx = pathParts[0] === scanDirectory.split('/').pop() ? 1 : 0;
      if (pathParts.length > startIdx + 1) {
        // Use the first subdirectory as phase
        phaseName = pathParts[startIdx].charAt(0).toUpperCase() + pathParts[startIdx].slice(1);
      }
    }
    
    // Initialize phase if needed
    if (!phaseMap.has(phaseName)) {
      phaseMap.set(phaseName, new Map());
    }
    
    const phase = phaseMap.get(phaseName)!;
    if (!phase.has(task.file)) {
      phase.set(task.file, []);
    }
    phase.get(task.file)!.push(task);
  });
  
  // Transform to PhaseGroups
  const phaseGroups: PhaseGroup[] = [];
  
  phaseMap.forEach((fileMap, phaseName) => {
    const allTasks: Task[] = [];
    const fileGroups = Array.from(fileMap.entries()).map(([path, fileTasks]) => {
      allTasks.push(...fileTasks);
      return {
        path: relative(process.cwd(), path),
        phase: phaseName,
        milestone: path.split('/').pop() || path,
        tasks: fileTasks,
        progress: calculateProgress(fileTasks)
      };
    });
    
    phaseGroups.push({
      name: phaseName,
      milestone: \`\${phaseName} Tasks\`,
      description: \`Tasks in \${phaseName}\`,
      fileGroups,
      totalTasks: allTasks.length,
      completedTasks: allTasks.filter(t => t.completed).length
    });
  });
  
  // Sort phase groups alphabetically, but keep "General" at the end
  phaseGroups.sort((a, b) => {
    if (a.name === 'General') return 1;
    if (b.name === 'General') return -1;
    return a.name.localeCompare(b.name);
  });
  
  return phaseGroups;
}

function calculateProgress(tasks: Task[]): number {
  if (tasks.length === 0) return 0;
  const completed = tasks.filter(t => t.completed).length;
  return Math.round((completed / tasks.length) * 100);
}

export async function GET(request: NextRequest) {
  const encoder = new TextEncoder();
  const clientId = \`client-\${Date.now()}-\${Math.random().toString(36).substr(2, 9)}\`;
  
  // Get settings from query params
  const url = new URL(request.url);
  const settings = {
    scanDirectory: url.searchParams.get('scanDirectory') || 'tickets',
    maxDepth: url.searchParams.get('maxDepth') || '5',
    filePatterns: url.searchParams.get('filePatterns') || '**/*.md,**/*.mdx',
    excludePatterns: url.searchParams.get('excludePatterns') || '**/node_modules/**,**/.git/**'
  };
  
  console.log('[stream] New client connected:', clientId, 'settings:', settings);
  
  const stream = new ReadableStream({
    async start(controller) {
      // Add this client to active connections
      clients.set(clientId, { controller, settings });
      
      // Send initial connection message
      const connectMessage = \`event: connected\\ndata: \${JSON.stringify({ 
        clientId,
        timestamp: new Date().toISOString() 
      })}\\n\\n\`;
      controller.enqueue(encoder.encode(connectMessage));
      
      // Send initial task data
      try {
        const tasks = await getProjectTasks(settings);
        const taskMessage = \`event: task-update\\ndata: \${JSON.stringify({ 
          tasks,
          timestamp: new Date().toISOString() 
        })}\\n\\n\`;
        controller.enqueue(encoder.encode(taskMessage));
      } catch (error) {
        console.error('[stream] Error getting initial tasks:', error);
      }
      
      // Set up file watcher for the scan directory
      const watchDir = join(process.cwd(), settings.scanDirectory);
      let watcher: AsyncIterable<any> | null = null;
      let watcherAbort: AbortController | null = null;
      
      // Function to set up the watcher
      const setupWatcher = async () => {
        try {
          // Check if directory exists first
          if (!existsSync(watchDir)) {
            console.log(\`[stream] Directory \${watchDir} does not exist yet, creating it...\`);
            mkdirSync(watchDir, { recursive: true });
          }
          
          watcherAbort = new AbortController();
          watcher = watch(watchDir, { recursive: true, signal: watcherAbort.signal });
          
          // Watch for file changes
          (async () => {
            try {
              for await (const event of watcher!) {
                if (event.filename && (event.filename.endsWith('.md') || event.filename.endsWith('.mdx'))) {
                  console.log('[stream] File change detected:', event.filename);
                  
                  // Debounce by 500ms to avoid rapid updates
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  // Get updated tasks
                  try {
                    const tasks = await getProjectTasks(settings);
                    const updateMessage = \`event: task-update\\ndata: \${JSON.stringify({ 
                      tasks,
                      timestamp: new Date().toISOString(),
                      trigger: 'file-change',
                      filename: event.filename
                    })}\\n\\n\`;
                    controller.enqueue(encoder.encode(updateMessage));
                  } catch (error) {
                    console.error('[stream] Error updating tasks:', error);
                  }
                }
              }
            } catch (error: any) {
              if (error.name !== 'AbortError') {
                console.error('[stream] Watcher error:', error);
              }
            }
          })();
        } catch (error) {
          console.error('[stream] Failed to setup watcher:', error);
        }
      };
      
      // Set up the watcher
      await setupWatcher();
      
      // Keep connection alive with heartbeat
      const heartbeat = setInterval(() => {
        try {
          const heartbeatMessage = \`event: heartbeat\\ndata: \${JSON.stringify({ 
            timestamp: new Date().toISOString() 
          })}\\n\\n\`;
          controller.enqueue(encoder.encode(heartbeatMessage));
        } catch (error) {
          // Client disconnected
          clearInterval(heartbeat);
          clients.delete(clientId);
        }
      }, 30000); // Send heartbeat every 30 seconds
      
      // Clean up on close
      request.signal.addEventListener('abort', () => {
        console.log('[stream] Client disconnected:', clientId);
        clearInterval(heartbeat);
        if (watcherAbort) {
          watcherAbort.abort();
        }
        clients.delete(clientId);
        controller.close();
      });
    },
  });
  
  return new Response(stream, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache, no-transform',
      'Connection': 'keep-alive',
      'X-Accel-Buffering': 'no', // Disable Nginx buffering
    },
  });
}
`;